import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface Transaction {
  date: string;
  description: string;
  category: string;
  amount: number;
}

interface ReportData {
  transactions: Transaction[];
  dateRange: { start: string; end: string };
  totalIncome: number;
  totalExpenses: number;
  savingsRate: string;
  monthlyData: Array<{
    month: string;
    income: number;
    expenses: number;
    balance: number;
  }>;
}

export const generatePDFReport = (data: ReportData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Brand colors (teal/cyan theme)
  const brandPrimary = [6, 182, 212]; // cyan-500
  const brandSecondary = [20, 184, 166]; // teal-500
  const brandDark = [15, 23, 42]; // slate-900
  const brandLight = [226, 232, 240]; // slate-200

  // Add watermark on every page
  const addWatermark = () => {
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(60);
    doc.setFont('helvetica', 'bold');
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.1 }));
    
    // Rotate and place watermark
    const centerX = pageWidth / 2;
    const centerY = pageHeight / 2;
    doc.text('GRANA EM DIA', centerX, centerY, {
      align: 'center',
      angle: 45,
    });
    doc.restoreGraphicsState();
  };

  // Page 1 - Cover Page with Power BI style
  const addCoverPage = () => {
    // Gradient-style header background (simulated with rectangles)
    doc.setFillColor(...brandPrimary);
    doc.rect(0, 0, pageWidth, 80, 'F');
    
    doc.setFillColor(6, 182, 212, 0.8);
    doc.rect(0, 60, pageWidth, 20, 'F');

    // Logo/Brand text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(32);
    doc.setFont('helvetica', 'bold');
    doc.text('GRANA EM DIA', pageWidth / 2, 35, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Relatório Financeiro Inteligente', pageWidth / 2, 48, { align: 'center' });

    // Main title
    doc.setTextColor(...brandDark);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('RELATÓRIO FINANCEIRO', pageWidth / 2, 110, { align: 'center' });

    // Date range
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const startDate = new Date(data.dateRange.start).toLocaleDateString('pt-BR');
    const endDate = new Date(data.dateRange.end).toLocaleDateString('pt-BR');
    doc.text(`Período: ${startDate} a ${endDate}`, pageWidth / 2, 125, { align: 'center' });

    // Power BI-style KPI Cards
    const cardY = 145;
    const cardWidth = 55;
    const cardHeight = 35;
    const spacing = 5;
    const startX = (pageWidth - (cardWidth * 3 + spacing * 2)) / 2;

    // Total Income Card
    doc.setFillColor(16, 185, 129, 0.1); // green background
    doc.roundedRect(startX, cardY, cardWidth, cardHeight, 3, 3, 'F');
    doc.setDrawColor(16, 185, 129);
    doc.setLineWidth(0.5);
    doc.roundedRect(startX, cardY, cardWidth, cardHeight, 3, 3, 'S');
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('RECEITAS TOTAIS', startX + cardWidth / 2, cardY + 10, { align: 'center' });
    doc.setFontSize(16);
    doc.setTextColor(16, 185, 129);
    doc.setFont('helvetica', 'bold');
    doc.text(`R$ ${data.totalIncome.toLocaleString('pt-BR')}`, startX + cardWidth / 2, cardY + 23, { align: 'center' });

    // Total Expenses Card
    const card2X = startX + cardWidth + spacing;
    doc.setFillColor(239, 68, 68, 0.1); // red background
    doc.roundedRect(card2X, cardY, cardWidth, cardHeight, 3, 3, 'F');
    doc.setDrawColor(239, 68, 68);
    doc.roundedRect(card2X, cardY, cardWidth, cardHeight, 3, 3, 'S');
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('SAÍDAS TOTAIS', card2X + cardWidth / 2, cardY + 10, { align: 'center' });
    doc.setFontSize(16);
    doc.setTextColor(239, 68, 68);
    doc.setFont('helvetica', 'bold');
    doc.text(`R$ ${data.totalExpenses.toLocaleString('pt-BR')}`, card2X + cardWidth / 2, cardY + 23, { align: 'center' });

    // Savings Rate Card
    const card3X = card2X + cardWidth + spacing;
    doc.setFillColor(59, 130, 246, 0.1); // blue background
    doc.roundedRect(card3X, cardY, cardWidth, cardHeight, 3, 3, 'F');
    doc.setDrawColor(59, 130, 246);
    doc.roundedRect(card3X, cardY, cardWidth, cardHeight, 3, 3, 'S');
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('TAXA DE POUPANÇA', card3X + cardWidth / 2, cardY + 10, { align: 'center' });
    doc.setFontSize(16);
    doc.setTextColor(59, 130, 246);
    doc.setFont('helvetica', 'bold');
    doc.text(`${data.savingsRate}%`, card3X + cardWidth / 2, cardY + 23, { align: 'center' });

    // Generated date
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'italic');
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, pageWidth / 2, cardY + 55, { align: 'center' });

    // Bia's signature - Power BI style
    const sigY = pageHeight - 60;
    
    // Signature box
    doc.setDrawColor(...brandPrimary);
    doc.setLineWidth(1);
    doc.line(20, sigY, pageWidth - 20, sigY);
    
    doc.setFontSize(11);
    doc.setTextColor(...brandPrimary);
    doc.setFont('helvetica', 'bold');
    doc.text('🤖 Análise Gerada por:', pageWidth / 2, sigY + 8, { align: 'center' });
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bolditalic');
    doc.text('Bia da Grana em Dia', pageWidth / 2, sigY + 18, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'italic');
    doc.text('Seu assistente financeiro inteligente', pageWidth / 2, sigY + 25, { align: 'center' });

    addWatermark();
  };

  // Page 2 - Monthly Summary Table
  const addMonthlySummary = () => {
    doc.addPage();
    addWatermark();

    // Header
    doc.setFillColor(...brandPrimary);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('RESUMO MENSAL', 20, 16);

    // Monthly table
    const monthlyTableData = data.monthlyData
      .filter(m => m.income > 0 || m.expenses > 0)
      .map(month => [
        month.month,
        `R$ ${month.income.toLocaleString('pt-BR')}`,
        `R$ ${month.expenses.toLocaleString('pt-BR')}`,
        `R$ ${month.balance.toLocaleString('pt-BR')}`
      ]);

    autoTable(doc, {
      startY: 35,
      head: [['Mês', 'Receitas', 'Saídas', 'Saldo']],
      body: monthlyTableData,
      theme: 'grid',
      headStyles: {
        fillColor: brandPrimary,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
      },
      bodyStyles: {
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250],
      },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { halign: 'right', textColor: [16, 185, 129] },
        2: { halign: 'right', textColor: [239, 68, 68] },
        3: { halign: 'right', fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        // Color code the balance column
        if (data.column.index === 3 && data.section === 'body') {
          const value = monthlyTableData[data.row.index][3];
          if (value.includes('-')) {
            data.cell.styles.textColor = [239, 68, 68]; // red for negative
          } else {
            data.cell.styles.textColor = [59, 130, 246]; // blue for positive
          }
        }
      },
    });
  };

  // Page 3 - Transactions Details
  const addTransactionsDetails = () => {
    doc.addPage();
    addWatermark();

    // Header
    doc.setFillColor(...brandPrimary);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('TRANSAÇÕES DETALHADAS', 20, 16);

    // Transactions table
    const transactionTableData = data.transactions.map(t => [
      new Date(t.date).toLocaleDateString('pt-BR'),
      t.description,
      t.category,
      `${t.amount > 0 ? '+' : ''}R$ ${Math.abs(t.amount).toLocaleString('pt-BR')}`
    ]);

    autoTable(doc, {
      startY: 35,
      head: [['Data', 'Descrição', 'Categoria', 'Valor']],
      body: transactionTableData,
      theme: 'grid',
      headStyles: {
        fillColor: brandPrimary,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: {
        fontSize: 8,
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250],
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 70 },
        2: { cellWidth: 40 },
        3: { halign: 'right', cellWidth: 35, fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        // Color code the amount column
        if (data.column.index === 3 && data.section === 'body') {
          const transaction = data.transactions[data.row.index];
          if (transaction.amount > 0) {
            data.cell.styles.textColor = [16, 185, 129]; // green for income
          } else {
            data.cell.styles.textColor = [239, 68, 68]; // red for expense
          }
        }
      },
      margin: { bottom: 30 },
    });

    // Footer on last page
    const finalY = (doc as any).lastAutoTable.finalY || 200;
    if (finalY < pageHeight - 40) {
      // Add Bia's signature at bottom of last page
      const footerY = pageHeight - 35;
      
      doc.setDrawColor(...brandPrimary);
      doc.setLineWidth(0.5);
      doc.line(20, footerY, pageWidth - 20, footerY);
      
      doc.setFontSize(9);
      doc.setTextColor(...brandPrimary);
      doc.setFont('helvetica', 'bold');
      doc.text('📊 Relatório gerado automaticamente por Bia da Grana em Dia', pageWidth / 2, footerY + 6, { align: 'center' });
      
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.setFont('helvetica', 'italic');
      doc.text('Seu assistente financeiro pessoal • Sempre ao seu lado nas decisões financeiras', pageWidth / 2, footerY + 12, { align: 'center' });
    }
  };

  // Generate all pages
  addCoverPage();
  addMonthlySummary();
  addTransactionsDetails();

  // Add page numbers to all pages except cover
  const pageCount = doc.getNumberOfPages();
  for (let i = 2; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'normal');
    doc.text(`Página ${i} de ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
  }

  // Save the PDF
  const fileName = `relatorio-grana-em-dia-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
